import type { NextRouter } from 'next/router';
import { advanceAfterUiSettle, dismissTourTargetModals, findTourTarget } from './AnchoredTourGuide';
import type { AnchoredTourStep } from './AnchoredTourGuide';
import { accountSettingsTour } from './accountSettingsTour';
import { addCandidateToSequenceTour } from './addCandidateToSequenceTour';
import { addSenderAccountTour } from './addSenderAccountTour';
import { configureCandidateEmailTour } from './configureCandidateEmailTour';
import { changeCandidateStatusTour } from './changeCandidateStatusTour';
import { connectCalendarTour } from './connectCalendarTour';
import { createJobFromUrlTour } from './createJobFromUrlTour';
import { createSequenceTour } from './createSequenceTour';
import { sendObserverLinkTour } from './sendObserverLinkTour';
import { sendSingleEmailTour } from './sendSingleEmailTour';
import { shareBookingLinkTour } from './shareBookingLinkTour';
import { startSequenceTour } from './startSequenceTour';

export type FormTourStep = AnchoredTourStep;

export type FormTourStepAction =
  | { type: 'navigate-if-needed'; advanceTo: number; waitForTargetTestIds?: readonly string[] }
  | {
      type: 'wait-for-target';
      advanceTo: number;
      waitForTargetTestIds: readonly string[];
      /** Snapshot row count before OAuth / async flows (persisted across full page reloads). */
      captureRowCountBaselineTestIds?: readonly string[];
      /** When true, advance as soon as the target appears even if it is already present on arrival. */
      autoAdvanceWhenTargetSatisfied?: boolean;
    }
  | { type: 'wait-for-target-absent'; advanceTo: number; waitForAbsentTestIds: readonly string[] }
  | { type: 'complete-when-target-absent'; waitForAbsentTestIds: readonly string[] }
  | {
      type: 'complete-when-target';
      waitForTargetTestIds: readonly string[];
      completeWhenCountIncreases?: boolean;
    }
  | {
      type: 'wait-for-target-or-complete';
      advanceTo: number;
      waitForTargetTestIds: readonly string[];
      completeWhenTargetTestIds: readonly string[];
      completeWhenCountIncreases?: boolean;
      /** When true, advance as soon as the advance target appears even if it is already present on arrival. */
      autoAdvanceWhenTargetSatisfied?: boolean;
    }
  | { type: 'next' }
  | { type: 'complete' };

export type FormTourConfig = {
  taskId: string;
  storageKey: string;
  startPath: string;
  overlineLabel: string;
  steps: readonly FormTourStep[];
  stepActions: Record<number, FormTourStepAction>;
  primaryLabels?: Record<number, string | ((pathname: string) => string)>;
  /** Early steps that use global nav (e.g. sidebar); tour is shown on any route until these finish. */
  navStepCount?: number;
  /** After nav steps, keep the tour active while pathname starts with this prefix. */
  activePathPrefix?: string;
  /** After nav steps, keep the tour active when pathname starts with any of these prefixes. */
  activePathPrefixes?: readonly string[];
  /** Show Mark Complete on the final step even when it uses completeWhenTargetClicked. */
  showMarkCompleteOnFinalStep?: boolean;
  /** Final step completes via a backend action on target click instead of /complete-onboarding-task. */
  completeTaskViaBackend?: boolean;
};

const isFormTourPathActive = (config: FormTourConfig, pathname: string) => {
  if (config.activePathPrefixes?.length) {
    return config.activePathPrefixes.some(prefix => pathname.startsWith(prefix));
  }

  if (config.activePathPrefix) {
    return pathname.startsWith(config.activePathPrefix);
  }

  return pathname === config.startPath;
};

export const isFormTourActiveOnPath = (config: FormTourConfig, pathname: string, stepIndex: number) => {
  const navCount = config.navStepCount ?? 0;

  if (stepIndex < navCount) {
    return true;
  }

  return isFormTourPathActive(config, pathname);
};

export const shouldDismissFormTourOnNavigate = (
  config: FormTourConfig,
  previousPathname: string,
  pathname: string,
  stepIndex: number,
) => {
  const navCount = config.navStepCount ?? 0;

  if (stepIndex < navCount) {
    return false;
  }

  if (config.activePathPrefixes?.length || config.activePathPrefix) {
    return isFormTourPathActive(config, previousPathname) && !isFormTourPathActive(config, pathname);
  }

  return previousPathname === config.startPath && pathname !== config.startPath;
};

export type FormTourActionContext = {
  config: FormTourConfig;
  stepIndex: number;
  router: NextRouter;
  goToStep: (stepIndex: number) => void;
  onComplete: () => void;
};

export const FORM_TOUR_CONFIGS = [
  accountSettingsTour,
  configureCandidateEmailTour,
  addSenderAccountTour,
  sendSingleEmailTour,
  createSequenceTour,
  addCandidateToSequenceTour,
  startSequenceTour,
  connectCalendarTour,
  shareBookingLinkTour,
  createJobFromUrlTour,
  changeCandidateStatusTour,
  sendObserverLinkTour,
] as const satisfies readonly FormTourConfig[];

export type RegisteredFormTourConfig = (typeof FORM_TOUR_CONFIGS)[number];

export const resolveFormTourHighlightTestIds = (step: FormTourStep): readonly string[] => {
  if (step.highlightTargetTestIds?.length) {
    return step.highlightTargetTestIds;
  }

  return step.targetTestIds;
};

const getFormTourStepWaitTargetTestIds = (
  action: FormTourStepAction | undefined,
): readonly string[] | null => {
  if (action?.type === 'wait-for-target' || action?.type === 'wait-for-target-or-complete') {
    return action.waitForTargetTestIds;
  }

  if (action?.type === 'complete-when-target') {
    return action.waitForTargetTestIds;
  }

  return null;
};

const isWaitTargetRelatedToHighlight = (waitId: string, highlightId: string) => {
  if (waitId === highlightId) {
    return true;
  }

  if (!waitId.endsWith('-selected')) {
    return false;
  }

  const stem = waitId.slice(0, -'-selected'.length);

  return (
    highlightId === stem ||
    highlightId.startsWith(`${stem}-`) ||
    highlightId.includes(stem.replace('@', ''))
  );
};

export const getFormTourStepInteractionTestIds = (
  config: FormTourConfig,
  stepIndex: number,
): readonly string[] => {
  const step = config.steps[stepIndex] as FormTourStep;
  const action = getFormTourStepAction(config, stepIndex);
  const waitTargetTestIds = getFormTourStepWaitTargetTestIds(action) ?? [];

  return [
    ...new Set([
      ...step.targetTestIds,
      ...(step.highlightTargetTestIds ?? []),
      ...waitTargetTestIds,
    ]),
  ];
};

export const isFormTourStepInteractionClick = (
  config: FormTourConfig,
  stepIndex: number,
  target: Element,
) =>
  getFormTourStepInteractionTestIds(config, stepIndex).some(testId =>
    Boolean(target.closest(`[data-testid="${testId}"]`)),
  );

export const isTourTargetClick = (target: Element, testIds: readonly string[]) =>
  testIds.some(testId => Boolean(target.closest(`[data-testid="${testId}"]`)));

/** Wait target matches what this step highlights — pause so the user can review or change the value. */
export const doesFormTourStepWaitForHighlightedTarget = (
  config: FormTourConfig,
  stepIndex: number,
) => {
  const action = getFormTourStepAction(config, stepIndex);
  const waitTargetTestIds = getFormTourStepWaitTargetTestIds(action);

  if (!waitTargetTestIds?.length) {
    return false;
  }

  const highlightTestIds = resolveFormTourHighlightTestIds(config.steps[stepIndex] as FormTourStep);

  return waitTargetTestIds.some(waitId =>
    highlightTestIds.some(highlightId => isWaitTargetRelatedToHighlight(waitId, highlightId)),
  );
};

/** Advance when a different UI target appears (navigation), not when configuring the highlighted control. */
export const isFormTourNavigationWaitStep = (config: FormTourConfig, stepIndex: number) => {
  const action = getFormTourStepAction(config, stepIndex);

  if (action?.type !== 'wait-for-target' && action?.type !== 'wait-for-target-or-complete') {
    return false;
  }

  return !doesFormTourStepWaitForHighlightedTarget(config, stepIndex);
};

export type FormTourAutoAdvanceRule = {
  advanceTo?: number;
  testIds: readonly string[];
  waitUntilAbsent?: boolean;
  completeTour?: boolean;
  completeWhenCountIncreases?: boolean;
  captureRowCountBaselineTestIds?: readonly string[];
};

export const getFormTourStepAction = (
  config: FormTourConfig,
  stepIndex: number,
): FormTourStepAction | undefined =>
  (config.stepActions as Record<number, FormTourStepAction | undefined>)[stepIndex];

export const getFormTourStepAutoAdvanceRules = (
  config: FormTourConfig,
  stepIndex: number,
): readonly FormTourAutoAdvanceRule[] => {
  const action = config.stepActions[stepIndex];

  if (!action) {
    return [];
  }

  if (action.type === 'wait-for-target') {
    return [
      {
        advanceTo: action.advanceTo,
        testIds: action.waitForTargetTestIds,
        captureRowCountBaselineTestIds: action.captureRowCountBaselineTestIds,
      },
    ];
  }

  if (action.type === 'wait-for-target-absent') {
    return [{ advanceTo: action.advanceTo, testIds: action.waitForAbsentTestIds, waitUntilAbsent: true }];
  }

  if (action.type === 'complete-when-target-absent') {
    return [{ testIds: action.waitForAbsentTestIds, waitUntilAbsent: true, completeTour: true }];
  }

  if (action.type === 'complete-when-target') {
    return [
      {
        testIds: action.waitForTargetTestIds,
        completeTour: true,
        completeWhenCountIncreases: action.completeWhenCountIncreases,
      },
    ];
  }

  if (action.type === 'wait-for-target-or-complete') {
    return [
      {
        testIds: action.completeWhenTargetTestIds,
        completeTour: true,
        completeWhenCountIncreases: action.completeWhenCountIncreases,
      },
      { advanceTo: action.advanceTo, testIds: action.waitForTargetTestIds },
    ];
  }

  if (action.type === 'navigate-if-needed' && action.waitForTargetTestIds?.length) {
    return [{ advanceTo: action.advanceTo, testIds: action.waitForTargetTestIds }];
  }

  return [];
};

export const getFormTourStepAutoAdvanceRule = (
  config: FormTourConfig,
  stepIndex: number,
): FormTourAutoAdvanceRule | null => getFormTourStepAutoAdvanceRules(config, stepIndex)[0] ?? null;

export const isFormTourAdvanceOnTargetStep = (config: FormTourConfig, stepIndex: number) => {
  const action = getFormTourStepAction(config, stepIndex);

  if (action?.type === 'wait-for-target' || action?.type === 'wait-for-target-or-complete') {
    return true;
  }

  if (action?.type === 'complete-when-target' && doesFormTourStepWaitForHighlightedTarget(config, stepIndex)) {
    return true;
  }

  return false;
};

export const shouldPauseFormTourStepForSatisfiedTarget = (
  config: FormTourConfig,
  stepIndex: number,
) => {
  const action = getFormTourStepAction(config, stepIndex);

  if (action?.type === 'wait-for-target') {
    if (action.autoAdvanceWhenTargetSatisfied) {
      return false;
    }

    if (!doesFormTourStepWaitForHighlightedTarget(config, stepIndex)) {
      return false;
    }

    return Boolean(findTourTarget(action.waitForTargetTestIds));
  }

  if (action?.type === 'wait-for-target-or-complete') {
    if (action.autoAdvanceWhenTargetSatisfied) {
      return false;
    }

    if (!doesFormTourStepWaitForHighlightedTarget(config, stepIndex)) {
      return false;
    }

    return Boolean(findTourTarget(action.waitForTargetTestIds));
  }

  if (action?.type === 'complete-when-target') {
    if (!doesFormTourStepWaitForHighlightedTarget(config, stepIndex)) {
      return false;
    }

    return Boolean(findTourTarget(action.waitForTargetTestIds));
  }

  return false;
};

export const isFormTourStepPausedForAutoAdvance = (
  config: FormTourConfig,
  stepIndex: number,
  pausedStepIndex: number | null,
) => {
  if (pausedStepIndex !== stepIndex) {
    return false;
  }

  return isFormTourAdvanceOnTargetStep(config, stepIndex);
};

/** Pause on arrival when the step target is already satisfied so the user can review or change values. */
export const getFormTourStepPauseIndexIfTargetSatisfied = (
  config: FormTourConfig,
  stepIndex: number,
): number | null => (shouldPauseFormTourStepForSatisfiedTarget(config, stepIndex) ? stepIndex : null);

const shouldPauseFormTourNavigationStepForSatisfiedTarget = (
  config: FormTourConfig,
  stepIndex: number,
  ignoreSatisfiedTestIds: readonly string[] = [],
) => {
  const action = getFormTourStepAction(config, stepIndex);

  if (action?.type !== 'wait-for-target' && action?.type !== 'wait-for-target-or-complete') {
    return false;
  }

  if (action.autoAdvanceWhenTargetSatisfied) {
    return false;
  }

  if (doesFormTourStepWaitForHighlightedTarget(config, stepIndex)) {
    return false;
  }

  const waitTargetTestIds = action.waitForTargetTestIds.filter(
    testId => !ignoreSatisfiedTestIds.includes(testId),
  );

  if (!waitTargetTestIds.length) {
    return false;
  }

  return Boolean(findTourTarget(waitTargetTestIds));
};

/** After Back, pause navigation wait steps whose target is still in the DOM so auto-advance does not skip them. */
export const getFormTourStepPauseIndexAfterBack = (
  config: FormTourConfig,
  stepIndex: number,
  options?: { ignoreSatisfiedTestIds?: readonly string[] },
): number | null => {
  const ignoreSatisfiedTestIds = options?.ignoreSatisfiedTestIds ?? [];

  if (shouldPauseFormTourStepForSatisfiedTarget(config, stepIndex)) {
    return stepIndex;
  }

  if (shouldPauseFormTourNavigationStepForSatisfiedTarget(config, stepIndex, ignoreSatisfiedTestIds)) {
    return stepIndex;
  }

  return null;
};

/** @deprecated Use isFormTourStepPausedForAutoAdvance */
export const isFormTourStepPausedForManualAdvance = isFormTourStepPausedForAutoAdvance;

/** Hide Next / Mark Complete when the user must click UI in the app; the tour advances when targets appear. */
export const shouldShowFormTourPrimaryButton = (
  config: FormTourConfig,
  stepIndex: number,
  pausedStepIndex: number | null = null,
) => {
  if (isFormTourStepPausedForAutoAdvance(config, stepIndex, pausedStepIndex)) {
    // Target already satisfied (e.g. after Back): show Next so the user can continue manually.
    return true;
  }

  const action = getFormTourStepAction(config, stepIndex);

  if (!action) {
    return true;
  }

  if (
    action.type === 'wait-for-target' ||
    action.type === 'wait-for-target-absent' ||
    action.type === 'complete-when-target-absent' ||
    action.type === 'complete-when-target' ||
    action.type === 'wait-for-target-or-complete'
  ) {
    return false;
  }

  if (action.type === 'next') {
    const step = config.steps[stepIndex] as FormTourStep;

    if (step.showPrimaryWhenTargetTestIds?.length) {
      return step.showPrimaryWhenTargetTestIds.some(testId => Boolean(findTourTarget([testId])));
    }

    return true;
  }

  if (action.type === 'complete') {
    if (config.showMarkCompleteOnFinalStep) {
      return true;
    }

    const step = config.steps[stepIndex];

    return !step.completeWhenTargetClicked?.length;
  }

  if (action.type === 'navigate-if-needed') {
    return false;
  }

  return true;
};

export const getFormTourPrimaryLabel = (config: FormTourConfig, stepIndex: number, pathname: string) => {
  const label = config.primaryLabels?.[stepIndex];

  if (typeof label === 'function') {
    return label(pathname);
  }

  if (label) {
    return label;
  }

  if (stepIndex === config.steps.length - 1) {
    return 'Mark Complete';
  }

  return 'Next';
};

export const runFormTourPrimaryAction = (
  ctx: FormTourActionContext,
  pausedStepIndex: number | null = null,
) => {
  const { config, stepIndex, router, goToStep, onComplete } = ctx;
  const action = config.stepActions[stepIndex];

  if (isFormTourStepPausedForAutoAdvance(config, stepIndex, pausedStepIndex)) {
    if (action?.type === 'wait-for-target' || action?.type === 'wait-for-target-or-complete') {
      goToStep(action.advanceTo);
    } else if (action?.type === 'complete-when-target') {
      onComplete();
    }

    return;
  }

  if (!action) {
    goToStep(stepIndex + 1);
    return;
  }

  if (action.type === 'navigate-if-needed') {
    if (router.pathname !== config.startPath) {
      void router.push(config.startPath);
      return;
    }

    if (action.waitForTargetTestIds?.length) {
      void advanceAfterUiSettle(action.advanceTo, goToStep, action.waitForTargetTestIds);
      return;
    }

    goToStep(action.advanceTo);
    return;
  }

  if (action.type === 'wait-for-target') {
    void advanceAfterUiSettle(action.advanceTo, goToStep, action.waitForTargetTestIds);
    return;
  }

  if (
    action.type === 'wait-for-target-absent' ||
    action.type === 'complete-when-target-absent' ||
    action.type === 'complete-when-target' ||
    action.type === 'wait-for-target-or-complete'
  ) {
    return;
  }

  if (action.type === 'next') {
    const step = config.steps[stepIndex] as FormTourStep;
    const closeOnNext = step.closeTargetTestIdsOnNext ?? [];
    goToStep(stepIndex + 1);
    if (closeOnNext.length) {
      dismissTourTargetModals(closeOnNext);
    }
    return;
  }

  if (action.type === 'complete') {
    onComplete();
    return;
  }

  onComplete();
};
